import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Button, Icon, MiniLoader } from '@pwc/pwc-deals-ui';
import { useTranslation } from 'react-i18next';
import { Droppable } from 'react-beautiful-dnd';
import _uniqBy from 'lodash/uniqBy';
import { TextUtilities } from '@pwc/pwc-deals-web-common';
import '../../../../common/components/ContentActions/RowActionsButton.scss';
import classNames from 'classnames';
import useMobile from 'common/hooks/useMobile';
import {
  getAnalysesGroupById,
  getAnalysesListForGroup,
} from '../../services/groupsService.ts';
import EditableGroupTitle from '../EditableGroupTitle/EditableGroupTitle';
import './AnalysesCustomList.scss';
import AnalysisItemDraggable from '../AnalysisItemDraggable/AnalysisItemDraggable';
import { getContentMessage, formatTitle } from '../../utils/groupsUtilities.ts';
import { GROUP_PAGE_SIZE } from '../../models/constants.ts';
import GroupMenu from '../GroupMenu/GroupMenu.tsx';
import GroupsPermissions from '../GroupsPermissions/GroupsPermissions.tsx';
import GroupErrorMessage from '../GroupErrorMessage/GroupErrorMessage.tsx';

const AnalysesCustomList = ({
  group,
  groupIndex,
  projectId,
  handleDeleteGroup,
  handleDeleteAnalysis,
  handleMoveAnalysis,
  handleRename,
  handleSelect,
  list = [],
  setList,
  selectedIDs,
  number,
  dragInProgress,
  isFirstGroup,
  isLastGroup,
  handleMoveDown,
  handleMoveUp,
  sourceId,
  tags,
  groupError,
  clearGroupError,
  permissions,
  enableDragAndDrop,
  groupErrorMessage,
  searchTitleDuplicity,
}) => {
  const isMobile = useMobile();
  const { title, contentCount, groupId } = group;
  const [isEditable, setIsEditable] = useState(false);
  const [oldTitle, setOldTitle] = useState(title);
  const [draftTitle, setDraftTitle] = useState(oldTitle);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const { isClientUser, isPwcReadOnlyRole, hasPermission } = permissions;
  const { t } = useTranslation();

  const toggleCollapsible = () => {
    setOpen(!open);
  };

  const handleEnter = () => {
    if (!open && dragInProgress) {
      setOpen(true);
    }
  };

  const handleExit = () => {
    if (
      open &&
      dragInProgress &&
      sourceId &&
      sourceId !== formatTitle(group.title)
    ) {
      setOpen(false);
    }
  };

  const handleKeyPress = ({ code }) => {
    if (code === 'Space') {
      toggleCollapsible();
    }
  };

  const handleLoadMore = () => {
    setPage(page + 1);
  };

  const showLoadMore =
    !loading && list.length < contentCount && contentCount > 0;

  const showLoader = list.length !== contentCount && loading;

  const handleControlDuplicityTitle = (newTitle) => {
    setDraftTitle(newTitle);
    searchTitleDuplicity(newTitle, groupId);
  };

  const handleCancelRename = () => {
    setIsEditable(false);
    setDraftTitle(oldTitle);
    clearGroupError();
  };

  const handleConfirmRename = () => {
    const trimmedDraftTitle = TextUtilities.trimText(draftTitle);
    if (trimmedDraftTitle) {
      clearGroupError();
      handleRename(trimmedDraftTitle, groupId);
    }
  };

  const handleGroupRenameMenu = () => {
    document.body.click();
    setIsEditable(true);
  };

  const handleGroupDeleteMenu = () => {
    document.body.click();
    handleDeleteGroup(groupId);
  };

  const handleMoveDownButton = () => handleMoveDown(groupId);
  const handleMoveUpButton = () => handleMoveUp(groupId);

  const handleMoveAnalysisFromGroup = (contentThreadId, sortOrder) => {
    handleMoveAnalysis(contentThreadId, sortOrder, groupId);
  };

  useEffect(() => {
    const getAnalysesList = async () => {
      const response = await getAnalysesListForGroup(
        projectId,
        groupId,
        page,
        GROUP_PAGE_SIZE,
        tags
      );

      setLoading(false);
      const items = response.items
        ? response.items.map((item) => {
            return {
              ...item,
              groupId,
            };
          })
        : response;
      const newItems = _uniqBy([...list, ...items], 'contentThreadId');
      setList(groupId, newItems);
    };

    if (open && contentCount > 0 && list.length < contentCount) {
      setLoading(true);
      getAnalysesList();
    }
    // eslint-disable-next-line
  }, [open]);

  /* Load more analyses on page change. */
  useEffect(() => {
    const getAnalysesList = async () => {
      const response = await getAnalysesGroupById(
        projectId,
        groupId,
        page,
        GROUP_PAGE_SIZE
      );

      setLoading(false);
      const items = response.items
        ? response.items.map((item) => {
            return { ...item, groupId };
          })
        : response;
      const newItems = _uniqBy([...list, ...items], 'contentThreadId');
      setList(groupId, newItems);
    };

    if (page > 0) {
      setLoading(true);
      getAnalysesList();
    }

    // eslint-disable-next-line
  }, [page]);

  useEffect(() => {
    if (isEditable) setOldTitle(TextUtilities.trimText(draftTitle));
    // eslint-disable-next-line
  }, [isEditable]);

  useEffect(() => {
    setOpen(false);
  }, [tags]);

  const droppableId = (title || '').toLowerCase().split(' ').join('-');

  const hasErrorDisplayed =
    groupError.hasRenameError && groupError.id === groupId;

  return (
    <div
      className="analyses-custom-list"
      onMouseEnter={handleEnter}
      onMouseLeave={handleExit}
    >
      <div className="analyses-custom-list__layout">
        {/* Is collapsible open. */}
        <Box
          className="analyses-custom-list__layout__open-indicator"
          onKeyDown={handleKeyPress}
          onClick={toggleCollapsible}
          role="button"
          tabIndex={0}
          sx={{
            paddingRight: isMobile ? '9px' : 'var(--s6)',
          }}
        >
          <Icon
            name={open ? 'desc' : 'down-chevron'}
            width="24"
            className="icon-orange"
          />
        </Box>
        <div
          className="analyses-custom-list__layout__title-section"
          onKeyDown={handleKeyPress}
          onClick={toggleCollapsible}
          role="button"
          tabIndex={0}
        >
          {/* Group title. */}
          <EditableGroupTitle
            groupId={groupId}
            number={number}
            title={draftTitle}
            isEditable={isEditable}
            setDraftTitle={handleControlDuplicityTitle}
            handleConfirmRename={handleConfirmRename}
          />
          {hasErrorDisplayed && (
            <div>
              <GroupErrorMessage message={groupErrorMessage} />
            </div>
          )}
          {/* Content count. */}
          <div className="content-count">
            {getContentMessage(contentCount, t)}
          </div>
        </div>

        {isEditable && (
          <GroupsPermissions projectId={projectId}>
            <div className="analyses-custom-list__layout__rename-controls">
              <Button className="button-menu" onClick={handleCancelRename}>
                {t('updateCancel')}
              </Button>
              <Button
                className="ui-button button-xs"
                onClick={handleConfirmRename}
                disabled={draftTitle.trim().length === 0 || hasErrorDisplayed}
              >
                {t('updateGroup')}
              </Button>
            </div>
          </GroupsPermissions>
        )}
        {!isEditable && (
          <div className="analyses-custom-list__layout__controls">
            {/* Context menu. */}
            {groupId !== -1 && (
              <div className="action-button-container">
                {!(isFirstGroup && isLastGroup) && !isClientUser && (
                  <div className="action-button-container__move">
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                    <div
                      className={classNames(
                        'button',
                        (isLastGroup ||
                          isPwcReadOnlyRole ||
                          !enableDragAndDrop) &&
                          'disabled'
                      )}
                      onClick={handleMoveDownButton}
                    >
                      <Icon name="move-down" width="24" className="outline" />
                    </div>
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                    <div
                      className={classNames(
                        'button',
                        (isFirstGroup ||
                          isPwcReadOnlyRole ||
                          !enableDragAndDrop) &&
                          'disabled'
                      )}
                      onClick={handleMoveUpButton}
                    >
                      <Icon name="move-up" width="24" className="outline" />
                    </div>
                  </div>
                )}
                <GroupsPermissions projectId={projectId}>
                  <GroupMenu
                    handleDelete={handleGroupDeleteMenu}
                    handleRename={handleGroupRenameMenu}
                    handleMoveUp={handleMoveUpButton}
                    handleMoveDown={handleMoveDownButton}
                    enableDragAndDrop={enableDragAndDrop}
                  />
                </GroupsPermissions>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Collapsible part (list of analyses or placeholder stuff). */}
      <Droppable key={droppableId} droppableId={droppableId}>
        {(provided) => {
          return (
            <div
              style={{
                height: open ? '100%' : 0,
                minHeight: open && dragInProgress ? '110px' : 'auto',
              }}
              key={droppableId}
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="analyses-custom-list__content"
            >
              <div
                style={{
                  visibility: open ? 'visible' : 'hidden',
                  marginTop: open ? 'var(--s4)' : '0',
                }}
              >
                {!showLoader && list.length === 0 && hasPermission && (
                  <div
                    className={classNames(
                      'analyses-custom-list__content__placeholder',
                      dragInProgress &&
                        'analyses-custom-list__content__placeholder-dragged-over'
                    )}
                  >
                    {t('dragAndDropAnalyses')}
                  </div>
                )}
                {open &&
                  list.map((item, index) => (
                    <AnalysisItemDraggable
                      item={item}
                      number={
                        groupId === -1
                          ? groupIndex + index
                          : `${groupIndex}.${index + 1}`
                      }
                      index={index}
                      key={item.contentThreadId}
                      selectedIDs={selectedIDs}
                      groupId={groupId}
                      projectId={projectId}
                      handleSelect={handleSelect}
                      handleDelete={handleDeleteAnalysis}
                      handleMove={handleMoveAnalysisFromGroup}
                      dragInProgress={dragInProgress}
                      permissions={permissions}
                      enableDragAndDrop={enableDragAndDrop}
                    />
                  ))}
                {list && list.length > 0 && provided.placeholder}
              </div>
            </div>
          );
        }}
      </Droppable>
      {open && showLoadMore && (
        <div className="load-more">
          <Button className="load-more__button" onClick={handleLoadMore}>
            {t('loadMore')}
          </Button>
        </div>
      )}
      {open && showLoader && <MiniLoader />}
    </div>
  );
};

export default AnalysesCustomList;
